import { AlertTriangle, ArrowRight, Inbox, Sparkles } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { useBookingRequests } from '@/hooks/useBookingRequests';
import { useCollections } from '@/hooks/useCollections';
import { useExperiences } from '@/hooks/useExperiences';
import { useActiveGuides } from '@/hooks/useGuides';
import { usePartners } from '@/hooks/usePartners';
import { formatDate } from '@/util/util_format';

/** Admin dashboard overview: what needs a decision, then the catalog at a glance. */
export function PageAdminDashboard() {
  const { experiences } = useExperiences();
  const { collections } = useCollections();
  const { partners } = usePartners();
  const { guides } = useActiveGuides();
  const { requests, pendingCount } = useBookingRequests();

  const active = experiences.filter((e) => e.status === 'active').length;

  // Requests already sorted newest first by the subscription.
  const pending = requests.filter((r) => r.status === 'pending');
  // A request whose notification email failed reached nobody's inbox, so the
  // dashboard is the only thing that will ever raise it.
  const unnotified = requests.filter((r) => r.notifyError).length;

  const stats = [
    { label: 'Pending requests', value: pendingCount },
    { label: 'Active experiences', value: active },
    { label: 'Collections', value: collections.length },
    { label: 'Partners', value: partners.length },
    { label: 'Tour leaders', value: guides.length },
  ];

  return (
    <div className='tw:mx-auto tw:max-w-6xl'>
      <header className='tw:mb-8'>
        <h1 className='tw:font-serif tw:text-3xl tw:font-light'>Dashboard</h1>
        <p className='tw:mt-1 tw:text-sm tw:text-muted-foreground'>
          Booking requests and an overview of your experiences. Invoices are coming next.
        </p>
      </header>

      {/* Top of the page, above the counts: the only thing here that is waiting
          on a person. */}
      {pendingCount > 0 && (
        <section className='tw:mb-6 tw:rounded-lg tw:border tw:border-ed-gold/40 tw:bg-ed-gold/10 tw:p-6'>
          <div className='tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4'>
            <div className='tw:flex tw:items-center tw:gap-3'>
              <Inbox className='tw:size-5 tw:text-ed-umber' />
              <div>
                <p className='tw:font-medium'>
                  {pendingCount === 1
                    ? '1 booking request is awaiting a decision'
                    : `${pendingCount} booking requests are awaiting a decision`}
                </p>
                <p className='tw:text-sm tw:text-muted-foreground'>
                  Partners are waiting on these — confirm the date you can hold, or decline.
                </p>
              </div>
            </div>
            <Button asChild>
              <Link to='/admin/requests'>
                Review <ArrowRight className='tw:size-4' />
              </Link>
            </Button>
          </div>

          {/* The three oldest waiting, so the page says what is waiting rather
              than only how much. */}
          <ul className='tw:mt-4 tw:divide-y tw:divide-ed-umber/15 tw:border-t tw:border-ed-umber/15'>
            {pending
              .slice(-3)
              .reverse()
              .map((request) => (
                <li key={request.id} className='tw:flex tw:flex-wrap tw:justify-between tw:gap-2 tw:py-2 tw:text-sm'>
                  <span>
                    <span className='tw:font-medium'>{request.partnerName}</span>
                    <span className='tw:text-muted-foreground'> · {request.experienceName}</span>
                  </span>
                  <span className='tw:text-xs tw:text-muted-foreground'>
                    {request.preferredDates.length > 1
                      ? `${request.preferredDates.length} dates offered`
                      : '1 date offered'}
                    {' · '}
                    {formatDate(request.createdAt)}
                  </span>
                </li>
              ))}
          </ul>
        </section>
      )}

      {unnotified > 0 && (
        <section className='tw:mb-6 tw:flex tw:items-center tw:gap-3 tw:rounded-lg tw:border tw:border-ed-red/30 tw:bg-ed-red/5 tw:p-4 tw:text-sm'>
          <AlertTriangle className='tw:size-5 tw:shrink-0 tw:text-ed-red' />
          <p>
            {unnotified === 1
              ? '1 request was recorded but its notification email did not send.'
              : `${unnotified} requests were recorded but their notification emails did not send.`}{' '}
            <Link to='/admin/requests' className='tw:underline tw:hover:text-ed-gold'>
              Check the queue
            </Link>{' '}
            — and the SendGrid key, if this keeps happening.
          </p>
        </section>
      )}

      <div className='tw:grid tw:gap-4 tw:sm:grid-cols-2 tw:lg:grid-cols-5'>
        {stats.map((stat) => (
          <div key={stat.label} className='tw:rounded-lg tw:border tw:bg-card tw:p-6'>
            <p className='tw:text-3xl tw:font-semibold'>{stat.value}</p>
            <p className='tw:mt-1 tw:text-sm tw:text-muted-foreground'>{stat.label}</p>
          </div>
        ))}
      </div>

      <div className='tw:mt-8 tw:flex tw:items-center tw:justify-between tw:rounded-lg tw:border tw:bg-card tw:p-6'>
        <div className='tw:flex tw:items-center tw:gap-3'>
          <Sparkles className='tw:size-5 tw:text-primary' />
          <div>
            <p className='tw:font-medium'>Manage experiences</p>
            <p className='tw:text-sm tw:text-muted-foreground'>Create, edit and organise the experience catalog.</p>
          </div>
        </div>
        <Button asChild>
          <Link to='/admin/experiences'>
            Open <ArrowRight className='tw:size-4' />
          </Link>
        </Button>
      </div>
    </div>
  );
}
